import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Building2 } from "lucide-react";

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const prefillEmail = params.get("email") || "";

  const { signup } = useAuth();
  const [form, setForm] = useState({ full_name: "", company_name: "", email: prefillEmail, password: "" });
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invitations/${inviteToken}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, email: r.data.email, company_name: r.data.org_name }));
      })
      .catch(() => toast.error("Invitation link is invalid or expired"));
  }, [inviteToken]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({ ...form, invite_token: inviteToken || undefined });
      toast.success(invite ? `Joined ${invite.org_name}!` : "Account created — welcome to RoofLead!");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-[#0B0F17] border-r border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30" />
        <Logo size="lg" />
        <div className="relative">
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-3">Live counter</p>
          <div className="font-display text-6xl font-extrabold text-white font-mono tracking-tight">$4.2M+</div>
          <p className="text-slate-300 mt-3 max-w-sm">of roofing revenue recovered by RoofLead operators in the last 30 days.</p>
          <ul className="mt-8 space-y-2 text-slate-300">
            <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-400" /> Full pipeline seeded on signup</li>
            <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-400" /> Real AI replies powered by Claude</li>
            <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-400" /> Team members with roles</li>
          </ul>
        </div>
        <p className="text-xs text-slate-500 relative">© 2026 RoofLead</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl">
          <div className="lg:hidden mb-6 flex justify-center"><Logo /></div>
          {invite ? (
            <>
              <div className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-slate-800/60 border border-slate-800">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-orange-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Joining</p>
                  <p className="font-semibold truncate">{invite.org_name}</p>
                </div>
                <Badge className="ml-auto bg-orange-500/15 text-orange-300 border-orange-500/30 capitalize">{invite.role}</Badge>
              </div>
              <h1 className="font-display text-2xl font-bold mb-2">Complete your account</h1>
              <p className="text-sm text-slate-400 mb-6">Create your login to join {invite.org_name}.</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold mb-2">Start recovering leads</h1>
              <p className="text-sm text-slate-400 mb-6">Create your company account, then finish onboarding.</p>
            </>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-slate-300">Full name</Label>
              <Input id="full_name" data-testid="signup-full-name" value={form.full_name} onChange={set("full_name")} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
            </div>
            <div>
              <Label htmlFor="company_name" className="text-slate-300">{invite ? "Organization" : "Company name"}</Label>
              <Input id="company_name" data-testid="signup-company" value={form.company_name} onChange={set("company_name")} readOnly={!!invite} placeholder="Apex Roofing Co." className="mt-1 bg-slate-800 border-slate-700 text-white" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300">Work email</Label>
              <Input id="email" data-testid="signup-email" type="email" value={form.email} onChange={set("email")} readOnly={!!invite} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" data-testid="signup-password" type="password" value={form.password} onChange={set("password")} minLength={6} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
              {loading ? "Creating account…" : (
                <>{invite ? `Join ${invite.org_name}` : "Create account"} <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            Have an account? <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="signup-goto-login">Log in</Link>
          </p>
          <p className="text-xs text-slate-500 mt-4 text-center leading-relaxed">
            By creating an account you agree to our <Link to="/terms" className="text-slate-400 hover:text-orange-400 underline" data-testid="signup-terms-link">Terms of Service</Link> and <Link to="/privacy" className="text-slate-400 hover:text-orange-400 underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
          </p>
        </Card>
      </div>
    </div>
  );
}
